import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  selectAllUserMaps,
  selectMapsLoading,
  selectMapsError,
  setCurrentMap,
  setViewMode,
} from "../../store/mapsSlice";
import { IMap } from "../../types";

interface MapGridViewProps {
  onCreateNewMap?: () => void;
  onEditMap?: (map: IMap) => void;
  onDuplicateMap?: (map: IMap) => void;
  onDeleteMap?: (map: IMap) => void;
}

const MapGridView: React.FC<MapGridViewProps> = ({
  onCreateNewMap,
  onEditMap,
  onDuplicateMap,
  onDeleteMap,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const allMaps = useSelector(selectAllUserMaps);
  const loading = useSelector(selectMapsLoading);
  const error = useSelector(selectMapsError);
  const [sortBy, setSortBy] = useState<
    "name" | "lastAccessed" | "created" | "accessCount"
  >("lastAccessed");
  const [searchTerm, setSearchTerm] = useState("");

  // Filter and sort maps
  const filteredMaps = allMaps
    .filter(
      (map) =>
        map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (map.description &&
          map.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (map.metadata?.buildingName &&
          map.metadata.buildingName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "lastAccessed":
          const aDate = a.lastAccessedAt
            ? new Date(a.lastAccessedAt).getTime()
            : 0;
          const bDate = b.lastAccessedAt
            ? new Date(b.lastAccessedAt).getTime()
            : 0;
          return bDate - aDate;
        case "created":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "accessCount":
          return (b.accessCount || 0) - (a.accessCount || 0);
        default:
          return 0;
      }
    });

  const handleMapSelect = (map: IMap) => {
    dispatch(setCurrentMap(map._id));
    dispatch(setViewMode("tabs"));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      language === "he" ? "he-IL" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const getMapPreview = (map: IMap) => {
    if (map.settings?.backgroundImage) {
      // For proxied S3 images, use full backend URL
      // For legacy uploaded images, use full API URL without /api suffix
      // For default images (stored in frontend public folder), use relative path
      const imageUrl = map.settings.backgroundImage.startsWith("/images/")
        ? `${process.env.REACT_APP_BASE_URL || "http://localhost:3051"}${
            map.settings.backgroundImage
          }?token=${localStorage.getItem("token")}`
        : map.settings.backgroundImage.startsWith("/uploads/")
        ? `${(
            process.env.REACT_APP_API_URL || "http://localhost:3051/api"
          ).replace("/api", "")}${map.settings.backgroundImage}`
        : map.settings.backgroundImage;

      return (
        <div
          className="w-full h-32 bg-cover bg-center bg-no-repeat rounded-t-lg"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      );
    }

    return (
      <div className="w-full h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-t-lg flex items-center justify-center">
        <svg
          className="w-12 h-12 text-blue-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">
            {language === "he" ? "טוען מפות..." : "Loading maps..."}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-gray-500 mb-4">
            {language === "he" ? "שגיאה בטעינת המפות" : "Error loading maps"}
          </p>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {language === "he" ? "כל המפות" : "All Maps"}
            </h1>
            <p className="text-gray-600">
              {language === "he"
                ? `${allMaps.length} מפות`
                : `${allMaps.length} maps`}
            </p>
          </div>

          {onCreateNewMap && (
            <button
              onClick={onCreateNewMap}
              className="btn-primary flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              {language === "he" ? "מפה חדשה" : "New Map"}
            </button>
          )}
        </div>

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={
                  language === "he" ? "חיפוש מפות..." : "Search maps..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="sm:w-48">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lastAccessed">
                {language === "he" ? "לפי גישה אחרונה" : "Last Accessed"}
              </option>
              <option value="name">
                {language === "he" ? "לפי שם" : "Name"}
              </option>
              <option value="created">
                {language === "he" ? "לפי תאריך יצירה" : "Date Created"}
              </option>
              <option value="accessCount">
                {language === "he" ? "לפי כמות גישות" : "Access Count"}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* Maps Grid */}
      {filteredMaps.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMaps.map((map) => (
            <div
              key={map._id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer group"
              onClick={() => handleMapSelect(map)}
            >
              {/* Map Preview */}
              {getMapPreview(map)}

              {/* Map Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                    {map.name}
                  </h3>

                  {/* Access Level Badge */}
                  <span
                    className={`
                                        inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                                        ${
                                          map.accessLevel === "owner"
                                            ? "bg-green-100 text-green-800"
                                            : map.accessLevel === "editor"
                                            ? "bg-blue-100 text-blue-800"
                                            : "bg-gray-100 text-gray-800"
                                        }
                                    `}
                  >
                    {map.accessLevel === "owner"
                      ? language === "he"
                        ? "בעלים"
                        : "Owner"
                      : map.accessLevel === "editor"
                      ? language === "he"
                        ? "עורך"
                        : "Editor"
                      : language === "he"
                      ? "צופה"
                      : "Viewer"}
                  </span>
                </div>

                {map.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {map.description}
                  </p>
                )}

                {/* Map Metadata */}
                {(map.metadata?.buildingName || map.metadata?.floor) && (
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {[map.metadata.buildingName, map.metadata.floor]
                      .filter(Boolean)
                      .join(" - ")}
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {map.accessCount || 0}{" "}
                    {language === "he" ? "צפיות" : "views"}
                  </div>

                  <div>
                    {map.lastAccessedAt
                      ? formatDate(map.lastAccessedAt)
                      : formatDate(map.createdAt)}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end mt-3 space-x-2 rtl:space-x-reverse opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {onEditMap && map.accessLevel !== "viewer" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditMap(map);
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title={language === "he" ? "ערוך מפה" : "Edit map"}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  )}

                  {onDuplicateMap && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicateMap(map);
                      }}
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title={language === "he" ? "שכפל מפה" : "Duplicate map"}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  )}

                  {onDeleteMap && map.accessLevel === "owner" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteMap(map);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title={language === "he" ? "מחק מפה" : "Delete map"}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : searchTerm ? (
        // No search results
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <p className="text-lg text-gray-500">
            {language === "he" ? "לא נמצאו מפות" : "No maps found"}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {language === "he"
              ? "נסה מילות חיפוש אחרות"
              : "Try different search terms"}
          </p>
        </div>
      ) : (
        // No maps at all
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
          </div>
          <p className="text-lg text-gray-500">
            {language === "he" ? "אין מפות עדיין" : "No maps yet"}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {language === "he"
              ? "צור את המפה הראשונה שלך"
              : "Create your first map to get started"}
          </p>
          {onCreateNewMap && (
            <button onClick={onCreateNewMap} className="mt-4 btn-primary">
              {language === "he" ? "צור מפה חדשה" : "Create New Map"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MapGridView;
